export type ContentLang = 'my' | 'en';

type AnyRecord = Record<string, unknown>;

function isRecord(value: unknown): value is AnyRecord {
  return typeof value === 'object' && value !== null;
}

function spansToMarkdown(children: unknown, markDefs: unknown): string {
  if (!Array.isArray(children)) return '';
  const defs = Array.isArray(markDefs) ? markDefs : [];
  return children
    .map((child) => {
      if (!isRecord(child)) return '';
      let text = typeof child.text === 'string' ? child.text : '';
      const marks = Array.isArray(child.marks) ? child.marks.filter((mark): mark is string => typeof mark === 'string') : [];
      if (marks.includes('strong')) text = `**${text}**`;
      if (marks.includes('em')) text = `*${text}*`;
      const link = marks
        .map((mark) => defs.find((def) => isRecord(def) && def._key === mark && typeof def.href === 'string'))
        .find((def): def is AnyRecord => isRecord(def));
      if (link && typeof link.href === 'string') text = `[${text}](${link.href})`;
      return text;
    })
    .join('');
}

function blocksToMarkdown(blocks: unknown[]): string {
  const lines: string[] = [];
  blocks.forEach((block) => {
    if (typeof block === 'string') {
      if (block.trim()) lines.push(block.trim());
      return;
    }
    if (!isRecord(block)) return;

    if (block._type === 'image' || block._type === 'lessonImage') {
      const asset = isRecord(block.asset) ? block.asset : undefined;
      const url = (typeof asset?.url === 'string' && asset.url) || (typeof block.url === 'string' ? block.url : '');
      const alt = typeof block.alt === 'string' ? block.alt : '';
      if (url) lines.push(`![${alt}](${url})`);
      return;
    }

    const text = spansToMarkdown(block.children, block.markDefs);
    if (!text.trim()) return;
    const listItem = typeof block.listItem === 'string' ? block.listItem : '';
    if (listItem === 'bullet') {
      lines.push(`- ${text}`);
      return;
    }
    if (listItem === 'number') {
      lines.push(`1. ${text}`);
      return;
    }
    const style = typeof block.style === 'string' ? block.style : 'normal';
    if (style === 'h1') lines.push(`# ${text}`);
    else if (style === 'h2') lines.push(`## ${text}`);
    else if (style === 'h3') lines.push(`### ${text}`);
    else if (style === 'h4') lines.push(`#### ${text}`);
    else if (style === 'blockquote') lines.push(`> ${text}`);
    else lines.push(text);
  });
  return lines.join('\n\n');
}

/** Turn markdown, plain text, or Portable Text into markdown. Never throws. */
export function toMarkdown(value: unknown): string {
  try {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
      if (value.length === 0) return '';
      if (value.every((item) => typeof item === 'string')) {
        return value.map((item) => String(item).trim()).filter(Boolean).join('\n\n');
      }
      return blocksToMarkdown(value).trim();
    }
    if (isRecord(value)) {
      if (Array.isArray(value.children) || value._type === 'block' || value._type === 'image') {
        return blocksToMarkdown([value]).trim();
      }
      if (typeof value.text === 'string') return value.text.trim();
    }
    return '';
  } catch {
    return '';
  }
}

export interface ResolvedField {
  text: string;
  /** True when the requested language was empty and the other language is shown. */
  fallback: boolean;
  lang: ContentLang;
}

/**
 * Burmese is the default. An empty Burmese field falls back to English, and the
 * reverse when the reader has chosen English.
 */
export function resolveField(
  lang: ContentLang,
  my?: string | null,
  en?: string | null,
  legacy?: unknown
): ResolvedField {
  const burmese = (my || '').trim();
  const englishText = (en || '').trim() || toMarkdown(legacy);

  if (lang === 'my') {
    if (burmese) return { text: burmese, fallback: false, lang: 'my' };
    if (englishText) return { text: englishText, fallback: true, lang: 'en' };
    return { text: '', fallback: false, lang: 'my' };
  }

  if (englishText) return { text: englishText, fallback: false, lang: 'en' };
  if (burmese) return { text: burmese, fallback: true, lang: 'my' };
  return { text: '', fallback: false, lang: 'en' };
}

export function fallbackNote(from: ContentLang): string {
  if (from === 'en') {
    return 'မြန်မာစာ မရှိသေးပါ။ အင်္ဂလိပ်စာဖြင့် ပြထားသည်။ English is shown because the Burmese text is empty.';
  }
  return 'အင်္ဂလိပ်စာ မရှိသေးပါ။ မြန်မာစာဖြင့် ပြထားသည်။';
}

/** Split markdown on blank lines so English and Burmese blocks can be paired. */
export function markdownBlocks(value: unknown): string[] {
  const markdown = toMarkdown(value);
  if (!markdown) return [];
  return markdown
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter(Boolean);
}
